import React, { useState, useMemo, useCallback, useRef } from "react";
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  TextField,
  Select,
  MenuItem,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  Menu,
  ListItemButton,
  ListItemText,
  Divider,
  Alert,
  Chip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import UndoIcon from "@mui/icons-material/Undo";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import VerifiedIcon from "@mui/icons-material/Verified";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import styles from "./outcome-modifier.module.scss";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import {
  setOutcomeModifierFeedback,
  resetOutcomeModifierFeedback,
} from "../../../store/filtersSlice";
import { sendMessageToAgent } from "../../../store/userSlice";
import { v4 as uuid } from "uuid";
import MarkdownRenderer from "../../MarkdownRenderer";
import DocumentCard from "../shared/DocumentCard";
import DocumentToolbar from "../shared/DocumentToolbar";
import SourceVerificationSlider from "../shared/SourceVerificationSlider";

interface SourceLocation {
  document_url?: string;
  page_number: number;
  polygon: number[];
  coordinate_system: string;
  page_width: number;
  page_height: number;
  unit: string;
}

interface ClauseReference {
  clause_number?: string;
  section_reference: string;
  clause_text_snippet: string;
  source_location?: SourceLocation;
  is_verified?: boolean;
}

interface ContractDetails {
  contract_type: string;
  contracting_parties: string[];
  agreement_date: string;
}

interface ActionItem {
  action: string;
  reason: string;
  responsible_party?: string;
  clause_reference?: string;
}

interface DocumentOutcome {
  document_name: string;
  contract_details: ContractDetails;
  clause_reference?: ClauseReference[];
  trigger_result: string;
  action_required: ActionItem[] | string | string[];
  consequence_summary: string;
  consequence_severity?: string;
  risk_tier: string;
  document_url?: string;
  is_verified?: boolean;
}

interface ParsedContent {
  finalAnswer?: {
    documents?: DocumentOutcome[];
  };
  question?: string;
}

interface OutcomeModifierAgentProps {
  parsedContent: ParsedContent;
  messageId?: string;
}

const OUTCOME_MODIFIER_ENDPOINT = "/stream/coc-review";

const getRiskTierColor = (riskTier: string): string => {
  const normalized = riskTier?.toLowerCase();
  if (normalized === "green" || normalized === "low") return "green";
  if (normalized === "red" || normalized === "high") return "red";
  if (normalized === "yellow" || normalized === "medium") return "medium";
  return "medium";
};

const formatContractingParties = (parties: string[]): string => {
  if (!parties || parties.length === 0) return "N/A";
  if (parties.length === 1) return parties[0];
  if (parties.length === 2) return `${parties[0]} and ${parties[1]}`;
  return `${parties.slice(0, -1).join(", ")}, and ${parties[parties.length - 1]}`;
};

export default function OutcomeModifierAgent({
  parsedContent,
  messageId,
}: OutcomeModifierAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const documents = parsedContent?.finalAnswer?.documents || [];

  const feedback = useSelector((state: RootState) =>
    messageId ? state.filters.outcomeModifierFeedback[messageId] : undefined,
  );
  const accept_array: any[] = feedback?.accept_array ?? [];
  const modify_array: any[] = feedback?.modify_array ?? [];

  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedValues, setEditedValues] = useState<Partial<DocumentOutcome>>(
    {},
  );
  const [sortBy, setSortBy] = useState("document-order");
  const [filterBy, setFilterBy] = useState("all");
  const [riskTierFilter, setRiskTierFilter] = useState("all");
  const [expandedDocs, setExpandedDocs] = useState<Record<number, boolean>>({});
  const [verificationStatus, setVerificationStatus] = useState<Record<number, boolean>>({});
  const [removedDocuments, setRemovedDocuments] = useState<number[]>([]);
  const [addBackAnchorEl, setAddBackAnchorEl] = useState<null | HTMLElement>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [sliderData, setSliderData] = useState<{
    documents: Array<{
      documentName: string;
      documentUrl?: string;
      clauses: ClauseReference[];
      documentIndex: number;
    }>;
    initialDocumentIndex: number;
  } | null>(null);

  const isLatestMessage =
    [...currentChat].reverse().find((m) => m.sender === "agent")?.id ===
    messageId;

  // Helper functions - defined before use
  const getDocumentKey = (index: number) => `doc_${index}`;

  const getDocumentStatus = (index: number) => {
    const key = getDocumentKey(index);
    if (accept_array.some((d) => d._key === key)) return "accepted";
    if (modify_array.some((d) => d._key === key)) return "modified";
    return null;
  };

  const getVerificationStatus = (doc: DocumentOutcome, index: number): boolean => {
    if (verificationStatus[index] !== undefined) return verificationStatus[index];
    return doc.is_verified ?? false;
  };

  const isDocumentHidden = (index: number): boolean => {
    return removedDocuments.includes(index);
  };

  // Handle document removal (hide it)
  const handleRemoveDocument = (index: number) => {
    if (!isDocumentHidden(index)) {
      setRemovedDocuments((prev) => [...prev, index]);
    }
  };

  // Handle document add back (unhide it)
  const handleAddBackDocument = (index: number) => {
    setRemovedDocuments((prev) => prev.filter((idx) => idx !== index));
    // Close menu
    setAddBackAnchorEl(null);
  };

  // Get list of hidden documents for the "Add back" menu
  const hiddenDocumentsList = useMemo(() => {
    return documents
      .map((doc, idx) => ({ doc, index: idx }))
      .filter(({ index }) => isDocumentHidden(index));
  }, [documents, removedDocuments]);

  // Get visible documents (not hidden) with sorting and filtering
  const visibleDocuments = useMemo(() => {
    // Step 1: Filter out hidden documents and map with original index
    let visible = documents
      .map((doc, idx) => ({ doc, originalIndex: idx }))
      .filter(({ originalIndex }) => !isDocumentHidden(originalIndex));

    // Step 2: Apply verification status filter
    if (filterBy !== "all") {
      visible = visible.filter(({ doc, originalIndex }) => {
        const status = getDocumentStatus(originalIndex);
        const isVerified = getVerificationStatus(doc, originalIndex);

        switch (filterBy) {
          case "verified":
            return isVerified || status === "accepted";
          case "not-verified":
            return !isVerified || status === "modified" || status === null;
          default:
            return true;
        }
      });
    }

    // Step 3: Apply risk tier filter
    if (riskTierFilter !== "all") {
      visible = visible.filter(({ doc }) => {
        const normalizedRiskTier = doc.risk_tier?.toLowerCase();
        switch (riskTierFilter) {
          case "high":
            return normalizedRiskTier === "high" || normalizedRiskTier === "red";
          case "medium":
            return normalizedRiskTier === "medium" || normalizedRiskTier === "yellow";
          case "low":
            return normalizedRiskTier === "low" || normalizedRiskTier === "green";
          default:
            return true;
        }
      });
    }

    // Step 4: Apply sorting
    const sorted = [...visible];
    const riskOrder: Record<string, number> = {
      high: 3, red: 3,
      medium: 2, yellow: 2,
      low: 1, green: 1
    };

    switch (sortBy) {
      case "risk-high":
        sorted.sort((a, b) => {
          const aRisk = riskOrder[a.doc.risk_tier?.toLowerCase()] || 0;
          const bRisk = riskOrder[b.doc.risk_tier?.toLowerCase()] || 0;
          return bRisk - aRisk;
        });
        break;
      case "risk-low":
        sorted.sort((a, b) => {
          const aRisk = riskOrder[a.doc.risk_tier?.toLowerCase()] || 0;
          const bRisk = riskOrder[b.doc.risk_tier?.toLowerCase()] || 0;
          return aRisk - bRisk;
        });
        break;
      default:
        // Keep document order
        break;
    }

    return sorted;
  }, [documents, sortBy, filterBy, riskTierFilter, accept_array, modify_array, removedDocuments]);

  const handleEdit = (doc: DocumentOutcome, index: number) => {
    setEditingIndex(index);

    // Check if this document has been modified before
    const key = getDocumentKey(index);
    const modifiedDoc = modify_array.find((d) => d._key === key);

    // Use modified values if they exist, otherwise use original document values
    const sourceDoc = modifiedDoc || doc;

    setEditedValues({
      trigger_result: sourceDoc.trigger_result || "",
      consequence_summary: sourceDoc.consequence_summary || "",
      risk_tier: sourceDoc.risk_tier || "",
      is_verified: getVerificationStatus(sourceDoc, index),
    });
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditedValues({});
  };

  const handleSaveEdit = (doc: DocumentOutcome, index: number) => {
    const key = getDocumentKey(index);

    const modifiedDoc = {
      ...doc,
      trigger_result: editedValues.trigger_result || doc.trigger_result,
      consequence_summary:
        editedValues.consequence_summary || doc.consequence_summary,
      risk_tier: editedValues.risk_tier || doc.risk_tier,
      is_verified: editedValues.is_verified ?? getVerificationStatus(doc, index),
      _key: key,
    };

    // Update verification status in local state
    if (editedValues.is_verified !== undefined) {
      setVerificationStatus((prev) => ({ ...prev, [index]: editedValues.is_verified! }));
    }

    // Check if values actually changed
    const hasChanges =
      editedValues.trigger_result !== doc.trigger_result ||
      editedValues.consequence_summary !== doc.consequence_summary ||
      editedValues.risk_tier !== doc.risk_tier ||
      (editedValues.is_verified !== undefined && editedValues.is_verified !== doc.is_verified);

    if (!messageId) return;

    if (hasChanges) {
      // Modified
      dispatch(
        setOutcomeModifierFeedback({
          messageId,
          feedback: {
            modify_array: [
              ...modify_array.filter((d) => d._key !== key),
              modifiedDoc,
            ],
            accept_array: accept_array.filter((d) => d._key !== key),
          },
        }),
      );
    } else {
      // Accepted (no changes)
      dispatch(
        setOutcomeModifierFeedback({
          messageId,
          feedback: {
            accept_array: [
              ...accept_array.filter((d) => d._key !== key),
              { ...doc, is_verified: getVerificationStatus(doc, index), _key: key },
            ],
            modify_array: modify_array.filter((d) => d._key !== key),
          },
        }),
      );
    }

    setEditingIndex(null);
    setEditedValues({});
  };

  const handleAccept = (doc: DocumentOutcome, index: number) => {
    if (!messageId) return;
    const key = getDocumentKey(index);

    // Accept the document as-is with current verification status
    dispatch(
      setOutcomeModifierFeedback({
        messageId,
        feedback: {
          accept_array: [
            ...accept_array.filter((d) => d._key !== key),
            { ...doc, is_verified: getVerificationStatus(doc, index), _key: key },
          ],
          modify_array: modify_array.filter((d) => d._key !== key),
        },
      }),
    );
  };

  const handleUndo = (index: number) => {
    if (!messageId) return;
    const key = getDocumentKey(index);

    // Remove from both arrays (undo the decision)
    dispatch(
      setOutcomeModifierFeedback({
        messageId,
        feedback: {
          accept_array: accept_array.filter((d) => d._key !== key),
          modify_array: modify_array.filter((d) => d._key !== key),
        },
      }),
    );
  };

  const handleResetFilters = useCallback(() => {
    setFilterBy("all");
    setRiskTierFilter("all");
  }, []);

  const handleCloseSlider = () => {
    setSliderData(null);
  };

  const handleClauseVerificationChange = (
    documentIndexInSlider: number,
    clauseIndex: number,
    isVerified: boolean
  ) => {
    if (!sliderData || !messageId) return;

    // Get the actual document index from the slider's document array
    const actualDocIndex = sliderData.documents[documentIndexInSlider].documentIndex;
    const doc = documents[actualDocIndex];

    if (doc && doc.clause_reference) {
      const updatedClauses = doc.clause_reference.map((clause, idx) => {
        if (idx === clauseIndex) {
          return { ...clause, is_verified: isVerified };
        }
        return clause;
      });

      // Update in modify_array
      const key = getDocumentKey(actualDocIndex);
      const modifiedDoc = modify_array.find((d) => d._key === key);

      if (modifiedDoc) {
        // Update existing entry in modify_array
        dispatch(
          setOutcomeModifierFeedback({
            messageId,
            feedback: {
              modify_array: modify_array.map((d) =>
                d._key === key ? { ...d, clause_reference: updatedClauses } : d
              ),
              accept_array,
            },
          }),
        );
      } else {
        // Add new entry to modify_array
        dispatch(
          setOutcomeModifierFeedback({
            messageId,
            feedback: {
              modify_array: [
                ...modify_array,
                { ...doc, clause_reference: updatedClauses, _key: key },
              ],
              accept_array: accept_array.filter((d) => d._key !== key),
            },
          }),
        );
      }
    }
  };

  // File upload handlers
  const handleFileUploadClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setFileUploadError(null);
    const newFiles: File[] = [];
    const errors: string[] = [];

    // Validate each file
    Array.from(files).forEach((file) => {
      // Check file type (PDF, DOCX, DOC)
      const validTypes = [
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/msword',
      ];

      if (!validTypes.includes(file.type) &&
          !file.name.toLowerCase().endsWith('.pdf') &&
          !file.name.toLowerCase().endsWith('.docx') &&
          !file.name.toLowerCase().endsWith('.doc')) {
        errors.push(`${file.name}: Only PDF and Word documents are allowed`);
        return;
      }

      // Check file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        errors.push(`${file.name}: File size exceeds 10MB limit`);
        return;
      }

      // Check for duplicate names
      if (uploadedFiles.some(f => f.name === file.name)) {
        errors.push(`${file.name}: File with same name already uploaded`);
        return;
      }

      newFiles.push(file);
    });

    if (errors.length > 0) {
      setFileUploadError(errors.join('; '));
    }

    if (newFiles.length > 0) {
      setUploadedFiles((prev) => [...prev, ...newFiles]);
    }

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [uploadedFiles]);

  const handleRemoveFile = useCallback((index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
    setFileUploadError(null);
  }, []);

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const handleApplyChanges = () => {
    if (!messageId) return;

    // Filter out hidden documents from the arrays before sending
    const visibleAcceptArray = accept_array.filter((doc) => {
      const docIndex = documents.findIndex((d) => d.document_name === doc.document_name);
      return !removedDocuments.includes(docIndex);
    });

    const visibleModifyArray = modify_array.filter((doc) => {
      const docIndex = documents.findIndex((d) => d.document_name === doc.document_name);
      return !removedDocuments.includes(docIndex);
    });

    const decisionsText = [
      visibleAcceptArray.length > 0
        ? `Accepted: ${visibleAcceptArray.length} document(s)`
        : "",
      visibleModifyArray.length > 0
        ? `Modified: ${visibleModifyArray.length} document(s)`
        : "",
      uploadedFiles.length > 0
        ? `Uploaded: ${uploadedFiles.length} new document(s)`
        : "",
    ]
      .filter(Boolean)
      .join(". ");

    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || 11,
        message: decisionsText || "Applied changes",
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: OUTCOME_MODIFIER_ENDPOINT,
        sessionId: null,
        accept_array: visibleAcceptArray,
        modify_array: visibleModifyArray,
        files: uploadedFiles.length > 0 ? uploadedFiles : undefined,
      }),
    );
    dispatch(resetOutcomeModifierFeedback(messageId));
  };

  if (documents.length === 0) {
    return (
      <div className={styles.container}>
        <p>No outcome data available.</p>
      </div>
    );
  }

  const totalDecisions = accept_array.length + modify_array.length;

  return (
    <div className={styles.container}>
      <div className={styles.headerSection}>
        <h2 className={styles.title}>Change of Control Outcome</h2>
      </div>

      <DocumentToolbar
        sortBy={sortBy}
        onSortChange={setSortBy}
        filterBy={filterBy}
        onFilterChange={setFilterBy}
        riskTierFilter={riskTierFilter}
        onRiskTierFilterChange={setRiskTierFilter}
        onResetFilters={handleResetFilters}
        instructionText="Review and modify outcome details for each document. Accept as-is or edit fields, then continue."
        showAddBack={hiddenDocumentsList.length > 0}
        onAddBack={(e) => setAddBackAnchorEl(e.currentTarget as HTMLElement)}
      />

      {/* Add Back Menu */}
      {hiddenDocumentsList.length > 0 && (
        <Menu
          anchorEl={addBackAnchorEl}
          open={Boolean(addBackAnchorEl)}
          onClose={() => setAddBackAnchorEl(null)}
          disableAutoFocusItem
          PaperProps={{
            sx: {
              maxHeight: 300,
              minWidth: 300,
            },
          }}
        >
          <Box sx={{ px: 2, py: 1, backgroundColor: "#f5f5f5" }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333" }}>
              Hidden Documents ({hiddenDocumentsList.length})
            </Typography>
          </Box>
          <Divider />
          {hiddenDocumentsList.map(({ doc, index }) => (
            <ListItemButton
              key={`hidden-${index}`}
              onClick={() => handleAddBackDocument(index)}
              sx={{
                py: 1.5,
                "&:hover": { backgroundColor: "rgba(0, 168, 136, 0.08)" },
              }}
            >
              <AddCircleOutlineIcon
                sx={{ mr: 1.5, color: "#00a888", fontSize: 20 }}
              />
              <ListItemText
                primary={doc.document_name}
                primaryTypographyProps={{
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              />
            </ListItemButton>
          ))}
        </Menu>
      )}

      {/* File Upload Section */}
      {isLatestMessage && (
        <Paper
          elevation={0}
          sx={{
            mb: 3,
            p: 2.5,
            border: "1px solid #e0e0e0",
            borderRadius: "8px",
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <AttachFileIcon sx={{ color: "#00a3a3" }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "#333" }}>
                Additional Documents
              </Typography>
              {uploadedFiles.length > 0 && (
                <Chip
                  label={`${uploadedFiles.length} file${uploadedFiles.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ backgroundColor: "#00a3a3", color: "white", fontWeight: 600 }}
                />
              )}
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={handleFileUploadClick}
              sx={{
                textTransform: "none",
                borderColor: "#00a3a3",
                color: "#00a3a3",
                "&:hover": {
                  borderColor: "#008a7a",
                  backgroundColor: "rgba(0, 163, 163, 0.04)",
                },
              }}
            >
              Upload Document
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              style={{ display: "none" }}
              onChange={handleFileChange}
            />
          </Box>

          {fileUploadError && (
            <Alert severity="error" onClose={() => setFileUploadError(null)} sx={{ mb: 2 }}>
              {fileUploadError}
            </Alert>
          )}

          {uploadedFiles.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ color: "#666", mb: 1, display: "block" }}>
                These documents will be included when you apply changes:
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {uploadedFiles.map((file, index) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      p: 1.5,
                      backgroundColor: "white",
                      border: "1px solid #e0e0e0",
                      borderRadius: "6px",
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flex: 1, minWidth: 0 }}>
                      <AttachFileIcon sx={{ color: "#666", fontSize: 20, flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            color: "#333",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.name}
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#999" }}>
                          {formatFileSize(file.size)}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => handleRemoveFile(index)}
                      sx={{
                        color: "#d32f2f",
                        "&:hover": {
                          backgroundColor: "rgba(211, 47, 47, 0.04)",
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                ))}
              </Box>
            </Box>
          )}

          {uploadedFiles.length === 0 && (
            <Typography
              variant="caption"
              sx={{
                color: "#999",
                fontStyle: "italic",
                display: "block",
                textAlign: "center",
              }}
            >
              Upload additional documents to be included in the analysis (PDF, Word formats supported, max 10MB per file)
            </Typography>
          )}
        </Paper>
      )}

      <div className={styles.documentsWrapper}>
        {visibleDocuments.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 8,
              px: 3,
              color: "#666",
            }}
          >
            <Typography variant="h6" sx={{ mb: 1, color: "#999" }}>
              No documents match your filters
            </Typography>
            <Typography variant="body2" sx={{ color: "#999" }}>
              Try adjusting your verification or risk tier filters to see more results.
            </Typography>
          </Box>
        ) : (
          visibleDocuments.map(({ doc, originalIndex }) => {
            const isEditing = editingIndex === originalIndex;
            const status = getDocumentStatus(originalIndex);

          return (
            <DocumentCard
              key={originalIndex}
              documentName={doc.document_name}
              riskTier={doc.risk_tier}
              isExpanded={expandedDocs[originalIndex] !== false}
              onToggleExpand={() =>
                setExpandedDocs((prev) => ({ ...prev, [originalIndex]: !prev[originalIndex] }))
              }
              onRemove={isLatestMessage ? () => handleRemoveDocument(originalIndex) : undefined}
            >
              <div className={styles.documentDetailsTable}>
                <table className={styles.detailsTable}>
                  <thead>
                    <tr>
                      <th>CONTRACT DETAILS</th>
                      <th>IDENTIFY COC TRIGGER</th>
                      <th>ACTION ITEMS</th>
                      <th>CONSEQUENCE OF NOT ADHERING TO COC REQUIREMENTS</th>
                      <th>RISK TIER</th>
                      {/* <th>REVIEW</th> */}
                      {isLatestMessage && <th>ACTIONS</th>}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className={styles.contractDetailsCell}>
                        {doc.contract_details ? (
                          <div className={styles.contractInfo}>
                            <div>
                              <strong>Type:</strong>{" "}
                              <span>{doc.contract_details.contract_type}</span>
                            </div>
                            <div>
                              <strong>Parties:</strong>{" "}
                              <span>
                                {formatContractingParties(
                                  doc.contract_details.contracting_parties,
                                )}
                              </span>
                            </div>
                            <div>
                              <strong>Date:</strong>{" "}
                              <span>{doc.contract_details.agreement_date}</span>
                            </div>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </td>
                      <td className={styles.editableCell}>
                        {isEditing ? (
                          <Select
                            fullWidth
                            size="small"
                            value={
                              editedValues.trigger_result ??
                              doc.trigger_result ??
                              ""
                            }
                            onChange={(e) =>
                              setEditedValues({
                                ...editedValues,
                                trigger_result: e.target.value,
                              })
                            }
                            sx={{
                              fontSize: "13px",
                              "& .MuiSelect-select": {
                                padding: "8px 14px",
                              },
                            }}
                          >
                            <MenuItem value="Triggered">Triggered</MenuItem>
                            <MenuItem value="Not triggered">Not triggered</MenuItem>
                            <MenuItem value="Review required">
                              Review required
                            </MenuItem>
                          </Select>
                        ) : (
                          <span>
                            {status === "modified"
                              ? modify_array.find(
                                  (d) => d._key === getDocumentKey(originalIndex),
                                )?.trigger_result || doc.trigger_result
                              : doc.trigger_result}
                          </span>
                        )}
                      </td>
                      <td className={styles.actionCell}>
                        {(() => {
                          const actionReq =
                            status === "modified"
                              ? modify_array.find(
                                  (d) => d._key === getDocumentKey(originalIndex),
                                )?.action_required || doc.action_required
                              : doc.action_required;

                          if (Array.isArray(actionReq)) {
                            return (
                              <ul>
                                {actionReq.map((item, idx) => {
                                  // Check if it's the new object format with action and reason
                                  if (typeof item === 'object' && item !== null && 'action' in item) {
                                    return (
                                      <li key={idx}>
                                        <strong>Action:</strong> {item.action}
                                        <br />
                                        <strong>Reason:</strong> {item.reason}
                                        {item.responsible_party && (
                                          <>
                                            <br />
                                            <strong>Responsible Party:</strong> {item.responsible_party}
                                          </>
                                        )}
                                        {item.clause_reference && (
                                          <>
                                            <br />
                                            <strong>Clause Reference:</strong> {item.clause_reference}
                                          </>
                                        )}
                                      </li>
                                    );
                                  }
                                  // Fallback for legacy string format
                                  return <li key={idx}>{item}</li>;
                                })}
                              </ul>
                            );
                          }

                          return actionReq || "N/A";
                        })()}
                      </td>
                      <td className={styles.consequenceCell} style={{ fontSize: "14px", lineHeight: 1.5 }}>
                        {isEditing ? (
                          <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            size="small"
                            value={editedValues.consequence_summary}
                            onChange={(e) =>
                              setEditedValues({
                                ...editedValues,
                                consequence_summary: e.target.value,
                              })
                            }
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                fontSize: "13px",
                              },
                            }}
                          />
                        ) : (
                          <>
                            <span>
                              {status === "modified"
                                ? modify_array.find(
                                    (d) => d._key === getDocumentKey(originalIndex),
                                  )?.consequence_summary || doc.consequence_summary
                                : doc.consequence_summary || "N/A"}
                            </span>
                            {doc.consequence_severity && (
                              <div style={{ marginTop: "8px", fontSize: "12px" }}>
                                Consequence Severity: {doc.consequence_severity}
                              </div>
                            )}
                          </>
                        )}
                      </td>
                      <td className={styles.centered}>
                        {isEditing ? (
                          <Select
                            size="small"
                            value={editedValues.risk_tier || doc.risk_tier}
                            onChange={(e) =>
                              setEditedValues({
                                ...editedValues,
                                risk_tier: e.target.value,
                              })
                            }
                            sx={{
                              minWidth: 100,
                              fontSize: "13px",
                              "& .MuiSelect-select": {
                                padding: "8px 14px",
                              },
                            }}
                          >
                            <MenuItem value="High">High</MenuItem>
                            <MenuItem value="Medium">Medium</MenuItem>
                            <MenuItem value="Low">Low</MenuItem>
                          </Select>
                        ) : (
                          <span
                            className={`${styles.riskBadge} ${
                              styles[
                                getRiskTierColor(
                                  status === "modified"
                                    ? modify_array.find(
                                        (d) => d._key === getDocumentKey(originalIndex),
                                      )?.risk_tier || doc.risk_tier
                                    : doc.risk_tier,
                                )
                              ]
                            }`}
                          ></span>
                        )}
                      </td>
                      {/* <td className={styles.reviewCell}>
                        {isEditing ? (
                          <ToggleButtonGroup
                            value={editedValues.is_verified ?? getVerificationStatus(doc, originalIndex) ? "verified" : "not-verified"}
                            exclusive
                            onChange={(e, newValue) => {
                              if (newValue !== null) {
                                setEditedValues({
                                  ...editedValues,
                                  is_verified: newValue === "verified",
                                });
                              }
                            }}
                            size="small"
                            className={styles.verificationToggle}
                          >
                            <ToggleButton value="verified" className={styles.verifiedToggleBtn}>
                              <VerifiedIcon fontSize="small" />
                              <span>Verified</span>
                            </ToggleButton>
                            <ToggleButton value="not-verified" className={styles.notVerifiedToggleBtn}>
                              <CancelIcon fontSize="small" />
                              <span>Not Verified</span>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        ) : (
                          <>
                            {getVerificationStatus(doc, originalIndex) ? (
                              <span className={styles.verifiedBadge}>
                                Verified
                              </span>
                            ) : (
                              <span className={styles.notVerifiedBadge}>
                                Not Verified
                              </span>
                            )}
                          </>
                        )}
                      </td> */}
                      {isLatestMessage && (
                        <td className={styles.actionsCell}>
                          <Box className={styles.actionsWrapper}>
                            {isEditing ? (
                              <>
                                <Tooltip title="Save">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleSaveEdit(doc, originalIndex)}
                                    className={styles.saveButton}
                                  >
                                    <CheckIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Cancel">
                                  <IconButton
                                    size="small"
                                    onClick={handleCancelEdit}
                                    className={styles.cancelButton}
                                  >
                                    <CloseIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </>
                            ) : (
                              <>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(doc, originalIndex)}
                                    className={styles.editButton}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>

                                {status === "accepted" ? (
                                  <>
                                    <Box className={styles.statusBadge}>
                                      <CheckCircleIcon
                                        sx={{ fontSize: 16, color: "#28a745" }}
                                      />
                                      <Typography className={styles.statusText}>
                                        Accepted
                                      </Typography>
                                    </Box>
                                    <Tooltip title="Undo">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleUndo(originalIndex)}
                                        className={styles.undoButton}
                                      >
                                        <UndoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : status === "modified" ? (
                                  <>
                                    <Box
                                      className={`${styles.statusBadge} ${styles.modifiedBadge}`}
                                    >
                                      <CheckCircleIcon
                                        sx={{ fontSize: 16, color: "#ffc107" }}
                                      />
                                      <Typography
                                        className={`${styles.statusText} ${styles.modifiedText}`}
                                      >
                                        Modified
                                      </Typography>
                                    </Box>
                                    <Tooltip title="Undo">
                                      <IconButton
                                        size="small"
                                        onClick={() => handleUndo(originalIndex)}
                                        className={styles.undoButton}
                                      >
                                        <UndoIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </>
                                ) : (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleAccept(doc, originalIndex)}
                                    className={styles.acceptButton}
                                  >
                                    Accept
                                  </Button>
                                )}
                              </>
                            )}
                          </Box>
                        </td>
                      )}
                    </tr>
                  </tbody>
                </table>
              </div>
            </DocumentCard>
          );
          })
        )}
      </div>

      {isLatestMessage && totalDecisions > 0 && (
        <Box className={styles.applySection}>
          <Box className={styles.decisionSummary}>
            <Typography className={styles.decisionSummaryText}>
              {accept_array.length > 0 && `${accept_array.length} accepted`}
              {accept_array.length > 0 && modify_array.length > 0 && ", "}
              {modify_array.length > 0 && `${modify_array.length} modified`}
            </Typography>
          </Box>
          <Button
            variant="contained"
            onClick={handleApplyChanges}
            className={styles.applyButton}
          >
            Apply Changes & Continue
          </Button>
        </Box>
      )}

      {/* Question Section */}
      {parsedContent?.question && (
        <Box className={styles.questionBox}>
          <Box className={styles.questionContent}>
            <MarkdownRenderer content={parsedContent.question} />
          </Box>
        </Box>
      )}

      {/* Source Verification Slider */}
      {sliderData && (
        <SourceVerificationSlider
          open={Boolean(sliderData)}
          onClose={handleCloseSlider}
          documents={sliderData.documents}
          initialDocumentIndex={sliderData.initialDocumentIndex}
          mode="interactive"
          onVerificationChange={handleClauseVerificationChange}
        />
      )}
    </div>
  );
}
